'use client';

import { BRAND } from '@/lib/cms/config';
import type { ProductImage } from '@/lib/product/types';
import clsx from 'clsx';
import Image from 'next/image';
import styles from './ProductStory.module.scss';

export interface ProductStoryProps {
  title?: string;
  /** Marketing body — empty/omitted still keeps the body shell for layout. */
  content?: string;
  image?: ProductImage;
  backgroundColor?: string;
  /** Image on left (true) or right (false, default). From CMS mediaAlignment. */
  mediaReversed?: boolean;
  /** Packshot alignment within the image panel (Gallet). Default: center. */
  imageAlign?: 'center' | 'right';
  className?: string;
}

/**
 * Parse heading into accent and rest parts for Breitling split-color styling
 * "THE STORY" → "THE" (accent, gold) + "STORY" (default text color)
 */
function parseHeading(heading: string): { accent: string; rest: string } {
  const words = heading.split(' ');
  if (words.length >= 2) {
    return { accent: words[0], rest: words.slice(1).join(' ') };
  }
  return { accent: '', rest: heading };
}

/**
 * Product story / model story section (text panel + optional image).
 * Rendered from CMS `PlaceholderModelStory` via CmsRenderer; Saleor supplies fill data.
 *
 * Layout: 2-column grid on desktop (text + media). Empty `content` still keeps the body shell
 * so title/image layout holds when marketing description is missing.
 */
export function ProductStory({
  title,
  content = '',
  image,
  backgroundColor,
  mediaReversed = false,
  imageAlign = 'center',
  className,
}: Readonly<ProductStoryProps>) {
  // Split content into paragraphs
  const paragraphs = content.split('\n\n').filter(Boolean);
  const { accent, rest } = parseHeading(title ?? '');

  return (
    <section className={clsx(styles.section, className)}>
      <div className={clsx(styles.container, mediaReversed && styles.mediaReversed)}>
        <div className={styles.textPanel} style={backgroundColor ? { backgroundColor } : undefined}>
          <div className={styles.content}>
            {title ? (
              <h2 className={styles.title}>
                {accent && <span className={styles.titleAccent}>{accent}</span>}
                {rest && ` ${rest}`}
              </h2>
            ) : null}
            {/* Always keep the body shell — empty when Saleor/CMS has no description. */}
            <div className={styles.body} aria-hidden={paragraphs.length === 0 ? true : undefined}>
              {paragraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>
        </div>

        {image ? (
          <div className={styles.imagePanel}>
            {BRAND === 'gallet' ? (
              <Image
                src={image.url}
                alt={image.alt}
                fill
                className={clsx(styles.image, imageAlign === 'right' && styles.imageAlignRight)}
                sizes="(max-width: 768px) 100vw, 40vw"
              />
            ) : (
              <Image
                src={image.url}
                alt={image.alt}
                width={image.width || 800}
                height={image.height || 525}
                className={styles.image}
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

ProductStory.displayName = 'ProductStory';
